// Stage 3: 面试组织&评估 - 5个模块
import { useApi, api } from '../api/client';
import { useActions } from '../actions';
import {
  PageHeader, ModuleBanner, Card, CardHead, Kpi, Tag, Status, Loading, ErrorBox, ScoreCell, RadarSVG,
} from '../components/UI';
import { MODULE_META } from '../config';

const ROUND_NAMES: Record<string, string> = {
  r1: '初面', r2: '复面', r3: '终面', r4: '待发 Offer', r5: '已发 Offer',
};

// ===== 3.1 邀约 =====
export function Invite() {
  const meta = MODULE_META['invite'];
  const { toast, openCreate } = useActions();
  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-primary" onClick={() => openCreate('interviews')}>+ 发起邀约</button>}
      />
      <ModuleBanner title="邀约工作台" stageTag="Stage 3-1" />

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Kpi label="待邀约" value={23} />
        <Kpi label="今日已邀" value={14} trend={{ type: 'up', text: '确认 11' }} />
        <Kpi label="确认率" value={78} unit="%" />
        <Kpi label="爽约率" value={6.2} unit="%" trend={{ type: 'up', text: '↓ 3%' }} />
      </div>

      <Card>
        <CardHead title="邀约记录" />
        <table className="table">
          <thead>
            <tr><th>候选人</th><th>应聘岗位</th><th>邀约时间</th><th>面试官</th><th>状态</th><th>操作</th></tr>
          </thead>
          <tbody>
            {[
              { name: '宋佳', role: '前端开发', time: '07-07 09:00', int: '陈婧', st: '已确认', av: '宋' },
              { name: '钱思源', role: '高级前端', time: '07-07 10:00', int: '前端 Leader', st: '已确认', av: '钱' },
              { name: '高雪', role: '前端开发', time: '07-07 11:00', int: '王晓萌', st: '待回复', av: '高' },
              { name: '黄子韬', role: 'iOS 工程师', time: '07-07 14:00', int: '林婉清', st: '已确认', av: '黄' },
              { name: '周雨桐', role: '数据分析师', time: '07-07 15:00', int: '陈婧', st: '时间冲突', av: '周' },
              { name: '韩冬', role: '前端开发', time: '07-08 10:00', int: '前端 Leader', st: '已确认', av: '韩' },
            ].map(r => (
              <tr key={r.name}>
                <td><div className="name-cell"><div className="avatar-sm">{r.av}</div><b>{r.name}</b></div></td>
                <td>{r.role}</td>
                <td className="mono text-xs">{r.time}</td>
                <td className="text-sm">{r.int}</td>
                <td>
                  <Status type={r.st === '已确认' ? 'done' : r.st === '待回复' ? 'progress' : 'warn'}>
                    {r.st}
                  </Status>
                </td>
                <td>
                  <button className="btn-text" onClick={() => toast(r.st === '已确认' ? '通知已发送' : r.st === '待回复' ? '邀约已重发' : '改期请求已提交')}>{r.st === '已确认' ? '通知' : r.st === '待回复' ? '重发' : '改期'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ===== 3.2 面试统筹 =====
export function InterviewArrange() {
  const meta = MODULE_META['interview-arrange'];
  const { openCreate } = useActions();
  const { data, loading, error } = useApi(() => api.interviews(), [], 'interviews');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  const grouped: Record<string, typeof data> = {};
  for (const it of data) {
    if (!grouped[it.round]) grouped[it.round] = [];
    grouped[it.round].push(it);
  }

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-primary" onClick={() => openCreate('interviews')}>+ 安排面试</button>}
      />
      <ModuleBanner title="今日面试看板" stageTag="Stage 3-2" />

      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <Kpi label="今日面试" value={12} unit="场" />
        <Kpi label="面试官" value={8} unit="位" />
        <Kpi label="会议室占用" value="5/8" />
        <Kpi label="到场率" value="93.8" unit="%" />
      </div>

      <div className="interview-board">
        {['r1', 'r2', 'r3', 'r4', 'r5'].map((rk, idx) => {
          const list = grouped[rk] || [];
          return (
            <div key={rk} className="ib-col">
              <div className="ib-col-head">
                <div className="ib-col-title">{ROUND_NAMES[rk]}</div>
                <span className="ib-col-count">{list.length}</span>
              </div>
              {list.map(it => (
                <div key={it.id} className={`ib-card r${idx + 1}`}>
                  <div className="ib-name">{it.name}</div>
                  <div className="ib-role">{it.role}</div>
                  <div className="ib-time">{it.time}</div>
                  <div className="ib-role" style={{ marginTop: 4 }}>@ {it.interviewer}</div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className="grid grid-2">
        <Card>
          <CardHead title="会议室占用" />
          <table className="table">
            <thead><tr><th>会议室</th><th>当前</th><th>下一场</th></tr></thead>
            <tbody>
              <tr><td>泰山 · 大</td><td><Status type="progress">使用中</Status></td><td className="text-sm">14:00 钱思源</td></tr>
              <tr><td>华山 · 中</td><td><Status type="done">空闲</Status></td><td className="text-sm">15:00 周雨桐</td></tr>
              <tr><td>黄山 · 中</td><td><Status type="progress">使用中</Status></td><td className="text-sm">16:00 团队会议</td></tr>
              <tr><td>峨眉 · 小</td><td><Status type="done">空闲</Status></td><td className="text-sm">—</td></tr>
              <tr><td>青城 · 小</td><td><Status type="done">空闲</Status></td><td className="text-sm">—</td></tr>
            </tbody>
          </table>
        </Card>
        <Card>
          <CardHead title="面试官今日排班" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {['陈婧 · HR 3 场', '王晓萌 · HR 2 场', '林婉清 · HR 2 场', '张* · 前端 Leader 2 场', '李* · 后端 Leader 1 场', 'CTO · 1 场', 'CEO · 1 场'].map(s => (
              <div key={s} className="flex justify-between items-center" style={{ padding: '10px 12px', background: 'var(--paper-2)', borderRadius: 6, fontSize: 12.5 }}>
                <span>{s}</span>
                <Tag color="bamboo">已确认</Tag>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ===== 3.3 分层面试评审 =====
export function InterviewEval() {
  const meta = MODULE_META['interview-eval'];
  const { data, loading, error } = useApi(() => api.evaluations(), [], 'evaluations');
  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
      />
      <ModuleBanner title="面试官评价汇总" stageTag="Stage 3-3" />

      <div className="grid grid-3" style={{ marginBottom: 24 }}>
        <Kpi label="HR 初面" value={128} unit="场" />
        <Kpi label="业务复面" value={62} unit="场" />
        <Kpi label="高管终面" value={28} unit="场" />
      </div>

      <Card>
        <CardHead title="面试评估表" />
        <table className="table eval-table">
          <thead>
            <tr>
              <th>候选人</th><th>岗位</th><th>轮次</th>
              <th>技术</th><th>沟通</th><th>文化</th><th>潜力</th><th>综合</th>
              <th>结论</th><th>评语</th>
            </tr>
          </thead>
          <tbody>
            {data.map(e => (
              <tr key={e.id}>
                <td><b>{e.name}</b></td>
                <td>{e.position}</td>
                <td><Tag color="outline">{e.round}</Tag></td>
                <td><ScoreCell score={e.technical} /></td>
                <td><ScoreCell score={e.communication} /></td>
                <td><ScoreCell score={e.culture} /></td>
                <td><ScoreCell score={e.potential} /></td>
                <td><b className="mono">{e.total}</b></td>
                <td>
                  <Tag color={e.result === '强烈推荐' || e.result === '推荐' ? 'bamboo' : e.result === '通过' ? 'cinnabar' : 'gold'}>
                    {e.result}
                  </Tag>
                </td>
                <td className="text-sm text-ink-3" style={{ maxWidth: 240 }}>{e.comment}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ===== 3.4 测评 =====
export function Assessment() {
  const meta = MODULE_META['assessment'];
  const { openCreate } = useActions();
  const { data: dims, loading: l1, error: e1 } = useApi(() => api.assessments(), [], 'assessments');
  const { data: records, loading: l2, error: e2 } = useApi(() => api.assessmentRecords(), [], 'assessment-records');
  if (l1 || l2) return <Loading />;
  if (e1 || e2 || !dims || !records) return <ErrorBox message={e1 || e2 || ''} />;

  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-primary" onClick={() => openCreate('assessment-records')}>+ 发起测评</button>}
      />
      <ModuleBanner title="候选人综合测评" stageTag="Stage 3-4" />

      <div className="grid grid-2" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: 24 }}>
        <Card>
          <CardHead
            title="六大维度评估 · 张明远"
            sub={<Tag color="cinnabar">综合 91.2</Tag>}
          />
          <div className="dim-grid">
            {dims.map(a => (
              <div key={a.id} className="dim-card">
                <div className="dim-head">
                  <div className="dim-name">{a.dim}</div>
                  <div className="dim-score">{a.score}</div>
                </div>
                <div className="dim-desc">{a.desc}</div>
                <div className="progress" style={{ height: 4, marginTop: 12 }}>
                  <div className="progress-bar" style={{ width: `${a.score}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <CardHead title="能力雷达图" />
          <div className="radar-wrap">
            <RadarSVG
              axes={['专业', '项目', '沟通', '潜力', '文化', '素质']}
              values={dims.map(d => d.score)}
            />
          </div>
          <div className="flex justify-between text-xs text-ink-3" style={{ padding: '0 20px', marginTop: 12 }}>
            {dims.map(d => <span key={d.id}>{d.dim} {d.score}</span>)}
          </div>
        </Card>
      </div>

      <Card>
        <CardHead title="测评记录" sub={`共 ${records.length} 项`} />
        <table className="table">
          <thead><tr><th>候选人</th><th>测评类型</th><th>得分/结果</th><th>用时</th><th>结论</th></tr></thead>
          <tbody>
            {records.map(r => (
              <tr key={r.id}>
                <td><b>{r.name}</b></td>
                <td>{r.type}</td>
                <td><b className="mono">{r.score}</b></td>
                <td className="text-sm text-ink-3">{r.time}</td>
                <td>
                  <Tag color={r.status === '优秀' ? 'bamboo' : 'cinnabar'}>{r.status}</Tag>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ===== 3.5 面试结果 =====
export function InterviewResult() {
  const meta = MODULE_META['interview-result'];
  const { toast } = useActions();
  return (
    <div className="fade-in">
      <PageHeader
        title={meta.title}
        subtitle={meta.subtitle}
        stageBadge={meta.stageBadge}
        actions={<button className="btn btn-ghost" onClick={() => toast('报告已生成')}>导出报告</button>}
      />
      <ModuleBanner title="本月面试结果汇总" stageTag="Stage 3-5" />

      <div className="grid grid-5" style={{ marginBottom: 24 }}>
        <Kpi label="面试总数" value={142} />
        <Kpi label="推荐录用" value={38} trend={{ type: 'up', text: '27%' }} />
        <Kpi label="待定" value={42} />
        <Kpi label="淘汰" value={48} trend={{ type: 'down', text: '34%' }} />
        <Kpi label="流失" value={14} />
      </div>

      <div className="grid grid-2">
        <Card>
          <CardHead title="面试官贡献 · 本月" />
          <table className="table">
            <thead><tr><th>面试官</th><th>面试数</th><th>推荐</th><th>通过率</th></tr></thead>
            <tbody>
              {[
                ['陈婧', 28, 12, 43],
                ['前端 Leader 张*', 22, 9, 41],
                ['后端 Leader 李*', 18, 7, 39],
                ['CTO', 12, 8, 67],
                ['CEO', 8, 5, 63],
                ['设计总监 林*', 14, 6, 43],
              ].map(([n, c, r, p]) => (
                <tr key={n as string}>
                  <td>{n}</td>
                  <td>{c}</td>
                  <td>{r}</td>
                  <td className="mono text-bamboo">{p}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card>
          <CardHead title="淘汰原因分布" />
          {[
            { label: '技术不达标', count: 18, pct: 38 },
            { label: '经验不匹配', count: 11, pct: 23 },
            { label: '薪资期望过高', count: 8, pct: 17 },
            { label: '稳定性存疑', count: 5, pct: 10 },
            { label: '文化不匹配', count: 4, pct: 8 },
            { label: '其他', count: 2, pct: 4 },
          ].map(r => (
            <div key={r.label} className="budget-bar">
              <div className="bb-label">{r.label}</div>
              <div className="bb-track">
                <div className="bb-fill" style={{ width: `${r.pct * 2}%`, background: 'var(--ink-2)' }}>
                  {r.count}
                </div>
              </div>
              <div className="bb-amount text-sm">{r.pct}%</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
